import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Typography, Spin, Tooltip } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import Swal from "sweetalert2";

import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import ReportDom from "../../../../../components/common/ReportDom";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import { salesOrderVouchersApi } from "../../../../../apiservice/order";
import SalesOrderCorporateVoucher from "../../SalesOrderVoucher/components/SalesOrderCorporateVoucher";
import SalesOrderVoucherBody from "../../SalesOrderVoucher/components/SalesOrderVoucherBody";
import { useShareDocument } from "../../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../../components/ShareButton/ShareDocumentButton";


const { Text } = Typography;

const QuickSalesOrderVoucher = ({authInfo}) => {
  const { id } = useParams(); // sale_o_id from URL
  const navigate = useNavigate();
  const reportRef = useRef(null); 

  const [loading, setLoading] = useState(false);
  const [institution, setInstitution] = useState(null);

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [vouchersList, setVouchersList] = useState([]);
  const [voucherNo, setVoucherNo] = useState("");
  const [accName, setAccName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Sales Order Voucher",
    removeAfterPrint: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch institution details
        const inst = await getInstitutionApi();
        setInstitution(inst.data);

        // 2. Fetch vouchers list
        const v = await salesOrderVouchersApi({});
        const list = v.data || [];
        setVouchersList(list);

        // 3. Auto pick the saved voucher
        const match = list.find((x) => String(x.sale_o_id) === String(id));
        if (match) {
          setSelectedVoucher(match);
        }
      } catch (err) {
        Swal.fire("Error", "Could not load voucher", "warning");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (shareOpen && customerEmail) {
      setEmail(customerEmail);
    }
  }, [shareOpen, customerEmail, setEmail]);

  return (
    <div className="p-4">
      {/* Header Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 8,
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/order/dashboard")}>
          Back
        </Button>

        {/* <div style={{ flex: 1, maxWidth: 400 }}>
          <SearchListAutoComplete
            entityType="voucher"
            placeholder="Search Voucher No"
            optionsList={(vouchersList || []).map((v) => ({
              sale_id: v.sale_o_id,
              display_text: v.display_text,
              raw: v,
            }))}
            onSelect={(obj) => setSelectedVoucher(obj?.raw || null)}
            showAddNew={false}
          />
        </div> */}

        <div style={{ display: "flex", gap: 8 }}>
          <ShareDocumentButton
            disabled={!selectedVoucher}
            shareOpen={shareOpen}
            setShareOpen={setShareOpen}
            email={email}
            setEmail={setEmail}
            loading={loading}
            onEmail={() =>
              handleEmailShare({
                url: `/print/sales-order/${selectedVoucher.sale_o_id}`,
                fileName: `SalesOrder_${
                  voucherNo ||
                  selectedVoucher.display_text ||
                  "Voucher"
                }.pdf`,
                name: accName || "",
                docNo:
                  voucherNo ||
                  selectedVoucher.display_text ||
                  "Voucher",
              })
            }
            onWhatsApp={() =>
              handleWhatsAppShare({
                url: `/print/sales-order/${selectedVoucher.sale_o_id}`,
                fileName: `SalesOrder_${
                  voucherNo ||
                  selectedVoucher.display_text ||
                  "Voucher"
                }.pdf`,
                text: `Hello ${accName || ""}, Your Sales Order ${
                  voucherNo ||
                  selectedVoucher.display_text ||
                  "Voucher"
                } is ready. Download here:`,
              })
            }
          />

          <Tooltip title={selectedVoucher?.approval_status === "pending" ? "Pending approval — Print is locked until fully approved" : ""}>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              disabled={!selectedVoucher || selectedVoucher?.approval_status === "pending"}
            >
              Print
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Voucher Display */}
      <Card className="shadow-md rounded-xl">
        {!selectedVoucher ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {loading ? <Spin /> : <Text type="secondary">No voucher selected</Text>}
          </div>
        ) 
        //  authInfo.branch_company_type === "Information Technology" ? (
        //   <SalesOrderCorporateVoucher
        //     sale_o_id={selectedVoucher.sale_o_id}
        //     institution={institution}
        //     authInfo={authInfo}
        //   />
        // ) 
        : (
          <SalesOrderVoucherBody
            sale_o_id={selectedVoucher.sale_o_id}
            institution={institution}
            setVoucherNo={setVoucherNo}
            setAccName={setAccName}
            setCustomerEmail={setCustomerEmail}
          />
        )}
      </Card>

      {/* Hidden print area */}
      {selectedVoucher && (
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReportDom
            ref={reportRef}
            // title="Sales Order Voucher"
            renderTable={() => (
              <SalesOrderVoucherBody
                sale_o_id={selectedVoucher.sale_o_id}
                institution={institution}
                printMode
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default QuickSalesOrderVoucher;
